import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function strength(pw) {
  const checks = [pw.length >= 8, /[a-zA-Z]/.test(pw), /\d/.test(pw)];
  return checks;
}

export default function ProfileSettings() {
  const { user, profile, refresh } = useAuth();
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [billing, setBilling] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    setFirst(profile?.first_name || "");
    setLast(profile?.last_name || "");
    setPhone(user?.phone || "");
    setCompany(profile?.company_name || "");
    setBilling(profile?.billing_address || "");
  }, [profile, user]);

  const profileDirty = useMemo(
    () =>
      first !== (profile?.first_name || "") ||
      last !== (profile?.last_name || "") ||
      phone !== (user?.phone || "") ||
      company !== (profile?.company_name || "") ||
      billing !== (profile?.billing_address || ""),
    [first, last, phone, company, billing, profile, user]
  );

  const pwChecks = strength(newPw);
  const pwAllOk = pwChecks.every(Boolean);
  const pwMatches = newPw && newPw === confirmPw;

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/profile", {
        first_name: first,
        last_name: last,
        phone,
        company_name: company,
        billing_address: billing,
      });
      await refresh?.();
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (!pwAllOk || !pwMatches) {
      toast.error("Please satisfy all password requirements.");
      return;
    }
    setSavingPw(true);
    try {
      await api.patch("/auth/password", {
        current_password: curPw,
        new_password: newPw,
      });
      toast.success("Password updated.");
      setCurPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPw(false);
    }
  };

  const isClient = user?.role === "client";
  const inputCls =
    "w-full bg-transparent border-b border-white/20 px-0 py-3 text-white placeholder-[#8E9AA6] focus:border-white focus:outline-none transition-colors text-sm";
  const labelCls = "block text-[11px] uppercase tracking-[0.25em] text-[#8E9AA6] mb-2";

  return (
    <div data-testid="profile-settings" className="space-y-10 max-w-3xl">
      {/* Card A — profile */}
      <form
        onSubmit={saveProfile}
        data-testid="profile-info-form"
        className="backdrop-blur-2xl bg-[#121214]/80 border border-white/10 p-8 md:p-10"
      >
        <div className="text-[11px] uppercase tracking-[0.35em] text-[#8E9AA6] mb-2">Account</div>
        <h2 className="font-serif text-2xl md:text-3xl text-white font-semibold tracking-tight mb-8">
          Profile Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
          <div>
            <label className={labelCls}>First Name</label>
            <input data-testid="profile-first-name" className={inputCls} value={first} onChange={(e) => setFirst(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Last Name</label>
            <input data-testid="profile-last-name" className={inputCls} value={last} onChange={(e) => setLast(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input data-testid="profile-phone" className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 (555) 555-1234" />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input className={inputCls + " opacity-60 cursor-not-allowed"} value={user?.email || ""} disabled />
          </div>
          {(isClient || company) && (
            <div className="md:col-span-2">
              <label className={labelCls}>Company {isClient && <span className="opacity-60">(optional)</span>}</label>
              <input data-testid="profile-company" className={inputCls} value={company} onChange={(e) => setCompany(e.target.value)} />
            </div>
          )}
          {isClient && (
            <div className="md:col-span-2">
              <label className={labelCls}>Billing Address (optional)</label>
              <textarea data-testid="profile-billing" rows={2} className={inputCls + " resize-none"} value={billing} onChange={(e) => setBilling(e.target.value)} />
            </div>
          )}
        </div>
        <button
          type="submit"
          data-testid="profile-save-btn"
          disabled={!profileDirty || savingProfile}
          className="mt-10 bg-white text-black hover:bg-[#E6E6E6] disabled:opacity-40 disabled:cursor-not-allowed px-8 py-3.5 transition-all duration-300 font-medium text-[11px] tracking-[0.25em] uppercase"
        >
          {savingProfile ? "Saving…" : "Save Changes"}
        </button>
      </form>

      {/* Card B — password */}
      <form
        onSubmit={savePassword}
        data-testid="password-form"
        className="backdrop-blur-2xl bg-[#121214]/80 border border-white/10 p-8 md:p-10"
      >
        <div className="text-[11px] uppercase tracking-[0.35em] text-[#8E9AA6] mb-2">Security</div>
        <h2 className="font-serif text-2xl md:text-3xl text-white font-semibold tracking-tight mb-8">
          Change Password
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
          <div className="md:col-span-2 max-w-md">
            <label className={labelCls}>Current Password</label>
            <input data-testid="password-current" type="password" className={inputCls} value={curPw} onChange={(e) => setCurPw(e.target.value)} autoComplete="current-password" />
          </div>
          <div>
            <label className={labelCls}>New Password</label>
            <input data-testid="password-new" type="password" className={inputCls} value={newPw} onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" />
            <ul className="mt-3 space-y-1 text-[11px] tracking-wide">
              {[
                ["At least 8 characters", pwChecks[0]],
                ["Includes a letter", pwChecks[1]],
                ["Includes a number", pwChecks[2]],
              ].map(([lbl, ok]) => (
                <li key={lbl} className={`flex items-center gap-2 ${ok ? "text-white" : "text-[#8E9AA6]"}`}>
                  <span className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-white" : "bg-[#8E9AA6]/40"}`} />
                  {lbl}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <label className={labelCls}>Confirm New Password</label>
            <input data-testid="password-confirm" type="password" className={inputCls} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} autoComplete="new-password" />
            {confirmPw && (
              <div className={`mt-3 text-[11px] tracking-wide ${pwMatches ? "text-white" : "text-red-400"}`}>
                {pwMatches ? "Passwords match." : "Passwords do not match."}
              </div>
            )}
          </div>
        </div>
        <button
          type="submit"
          data-testid="password-save-btn"
          disabled={!curPw || !pwAllOk || !pwMatches || savingPw}
          className="mt-10 bg-white text-black hover:bg-[#E6E6E6] disabled:opacity-40 disabled:cursor-not-allowed px-8 py-3.5 transition-all duration-300 font-medium text-[11px] tracking-[0.25em] uppercase"
        >
          {savingPw ? "Updating…" : "Update Password"}
        </button>
      </form>
    </div>
  );
}
